import { ArrowRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';

const DISMISS_KEY = 'sticky-cta-dismissed';

/**
 * Mobile-only CTA bar that slides up once the visitor scrolls past the hero
 * (where the primary CTA has left the viewport). Dismissal sticks for the
 * session. Desktop keeps the header CTA, so this renders md:hidden.
 */
export function StickyMobileCta(): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  useEffect(() => {
    if (dismissed) return;
    const onScroll = (): void => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur transition-transform duration-300 motion-reduce:transition-none md:hidden ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">Never miss another call</p>
          <p className="truncate text-xs text-muted-foreground">
            From $39/month · Live in minutes
          </p>
        </div>
        <Button asChild size="sm">
          <Link
            to="/signup"
            tabIndex={visible ? 0 : -1}
            onClick={() => track('sticky_cta_click')}
          >
            Get started <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          tabIndex={visible ? 0 : -1}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          onClick={() => {
            sessionStorage.setItem(DISMISS_KEY, '1');
            setDismissed(true);
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
